import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserX, Clock, CheckCircle2, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type DeletionRequest = {
  id: string;
  userId: string;
  emailSnapshot: string | null;
  nameSnapshot: string | null;
  source: "user" | "inactivity";
  status: "pending" | "completed" | "cancelled";
  requestedAt: string;
  resolvedAt: string | null;
  resolvedByAdminId: string | null;
  notes: string | null;
};

type Stats = {
  pending: number;
  completed: number;
  cancelled: number;
  total: number;
};

type AdminDeletionRequestsResponse = {
  stats: Stats;
  requests: DeletionRequest[];
};

function formatDate(s: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function SourceBadge({ source }: { source: DeletionRequest["source"] }) {
  if (source === "inactivity") return <Badge variant="secondary" className="text-xs">Inactivity</Badge>;
  return <Badge variant="outline" className="text-xs">Self-service</Badge>;
}

function StatusBadge({ status }: { status: DeletionRequest["status"] }) {
  if (status === "pending") return <Badge className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">Pending</Badge>;
  if (status === "completed") return <Badge className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Anonymised</Badge>;
  return <Badge variant="secondary" className="text-xs">Cancelled</Badge>;
}

export default function AdminAccountDeletionsPage() {
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [confirmTarget, setConfirmTarget] = useState<DeletionRequest | null>(null);
  const [confirmEmailInput, setConfirmEmailInput] = useState("");

  const { data, isLoading } = useQuery<AdminDeletionRequestsResponse>({
    queryKey: ["/api/admin/deletion-requests", statusFilter, sourceFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (sourceFilter !== "all") params.set("source", sourceFilter);
      const res = await apiRequest("GET", `/api/admin/deletion-requests?${params.toString()}`);
      return res.json();
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/admin/deletion-requests/${id}/cancel`, {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/deletion-requests"] });
      toast({ title: "Deletion request cancelled" });
    },
    onError: (err: any) => {
      toast({ title: "Failed to cancel request", description: err?.message, variant: "destructive" });
    },
  });

  const confirmMutation = useMutation({
    mutationFn: async ({ id, confirmEmail }: { id: string; confirmEmail: string }) => {
      const res = await apiRequest("POST", `/api/admin/deletion-requests/${id}/confirm`, { confirmEmail });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/deletion-requests"] });
      setConfirmTarget(null);
      setConfirmEmailInput("");
      toast({ title: "Account anonymised", description: "Personal data has been scrubbed. Financial records were retained." });
    },
    onError: (err: any) => {
      toast({ title: "Failed to confirm deletion", description: err?.message, variant: "destructive" });
    },
  });

  const stats = data?.stats;
  const requests = data?.requests ?? [];

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-lg sm:text-2xl font-bold">Account Deletion Requests</h1>
        <p className="text-muted-foreground text-sm">
          Review and confirm account deletion requests. Confirming anonymises personal data — financial and
          subscription records are retained for 6 years.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="h-8 w-8 text-amber-500 shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Pending</p>
              <p className="text-2xl font-bold">{stats?.pending ?? "—"}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle2 className="h-8 w-8 text-green-600 shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Anonymised</p>
              <p className="text-2xl font-bold">{stats?.completed ?? "—"}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <XCircle className="h-8 w-8 text-muted-foreground shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Cancelled</p>
              <p className="text-2xl font-bold">{stats?.cancelled ?? "—"}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <UserX className="h-8 w-8 text-primary shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">{stats?.total ?? "—"}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Requests</CardTitle>
          <CardDescription>Filter by status or how the request was raised</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 h-8 text-xs">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Anonymised</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-40 h-8 text-xs">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="user">Self-service</SelectItem>
                <SelectItem value="inactivity">Inactivity</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Loading...</p>
          ) : requests.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No deletion requests match the current filters</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Email</TableHead>
                    <TableHead className="text-xs">Name</TableHead>
                    <TableHead className="text-xs">Source</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Requested</TableHead>
                    <TableHead className="text-xs">Processed</TableHead>
                    <TableHead className="text-xs w-48" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-xs font-medium">{r.emailSnapshot ?? "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{r.nameSnapshot ?? "—"}</TableCell>
                      <TableCell><SourceBadge source={r.source} /></TableCell>
                      <TableCell><StatusBadge status={r.status} /></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDate(r.requestedAt)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDate(r.resolvedAt)}</TableCell>
                      <TableCell>
                        {r.status === "pending" && (
                          <div className="flex items-center gap-1 justify-end">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs h-7"
                              onClick={() => cancelMutation.mutate(r.id)}
                              disabled={cancelMutation.isPending}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              className="text-xs h-7"
                              onClick={() => { setConfirmTarget(r); setConfirmEmailInput(""); }}
                            >
                              Confirm & Anonymise
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmTarget} onOpenChange={(open) => { if (!open) { setConfirmTarget(null); setConfirmEmailInput(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently anonymise this account?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  This will scrub personal data (name, email, address, client and quote details) for{" "}
                  <strong>{confirmTarget?.emailSnapshot}</strong>. Financial and subscription records are
                  retained for 6 years. This action cannot be undone.
                </p>
                <p>Type the account's exact email address to confirm:</p>
                <Input
                  value={confirmEmailInput}
                  onChange={(e) => setConfirmEmailInput(e.target.value)}
                  placeholder={confirmTarget?.emailSnapshot ?? "user@example.com"}
                  data-testid="input-confirm-delete-email"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-confirm-dialog">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={
                !confirmTarget ||
                confirmEmailInput.trim().toLowerCase() !== (confirmTarget.emailSnapshot ?? "").toLowerCase() ||
                confirmMutation.isPending
              }
              onClick={(e) => {
                e.preventDefault();
                if (!confirmTarget) return;
                confirmMutation.mutate({ id: confirmTarget.id, confirmEmail: confirmEmailInput.trim() });
              }}
              data-testid="button-confirm-anonymise"
            >
              {confirmMutation.isPending ? "Anonymising..." : "Anonymise Account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
